// Firestore Portfolio Service for BICE integration
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  limit,
  query,
  documentId,
  where
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

function getPortfolioCollectionRef(userId, entityId) {
  if (!userId || !entityId) throw new Error('userId and entityId are required');
  return collection(
    db,
    `users/${userId}/entities/${entityId}/investments/bice/portfolio`
  );
}

// Returns the min and max year available based on the first and last document IDs (yyyy-mm-dd)
export async function getPortfolioYearRange(userId, entityId) {
  const col = getPortfolioCollectionRef(userId, entityId);
  const firstQ = query(col, orderBy(documentId(), 'asc'), limit(1));
  const lastQ = query(col, orderBy(documentId(), 'desc'), limit(1));
  const [firstSnap, lastSnap] = await Promise.all([getDocs(firstQ), getDocs(lastQ)]);
  if (firstSnap.empty || lastSnap.empty) return { minYear: null, maxYear: null, years: [] };
  const firstId = firstSnap.docs[0].id; // yyyy-mm-dd
  const lastId = lastSnap.docs[0].id;
  const minYear = Number(String(firstId).slice(0, 4));
  const maxYear = Number(String(lastId).slice(0, 4));
  if (!Number.isFinite(minYear) || !Number.isFinite(maxYear)) return { minYear: null, maxYear: null, years: [] };
  const years = [];
  for (let y = maxYear; y >= minYear; y--) years.push(y);
  return { minYear, maxYear, years };
}

export async function getLatestPortfolio(userId, entityId) {
  const col = getPortfolioCollectionRef(userId, entityId);
  // Order by document ID (yyyy-mm-dd)
  const q = query(col, orderBy(documentId(), 'desc'), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  return { id: docSnap.id, ...docSnap.data() };
}

export async function getPortfolioByDate(userId, entityId, dateId) {
  // dateId format: yyyy-mm-dd
  const ref = doc(db, `users/${userId}/entities/${entityId}/investments/bice/portfolio/${dateId}`);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getPortfolioHistory(userId, entityId, max = 90) {
  const col = getPortfolioCollectionRef(userId, entityId);
  const q = query(col, orderBy(documentId(), 'desc'), limit(max));
  const snap = await getDocs(q);
  const results = [];
  snap.forEach(d => results.push({ id: d.id, ...d.data() }));
  // Return chronologically ascending for charting
  return results.reverse();
}

export async function getPortfolioHistoryForYear(userId, entityId, year) {
  const col = getPortfolioCollectionRef(userId, entityId);
  const startId = `${year}-01-01`;
  const endId = `${year}-12-31`;
  // Query all docs within the year range, ordered ascending by date-id (yyyy-mm-dd)
  const q = query(
    col,
    orderBy(documentId(), 'asc'),
    where(documentId(), '>=', startId),
    where(documentId(), '<=', endId)
  );
  const snap = await getDocs(q);
  const results = [];
  snap.forEach(d => results.push({ id: d.id, ...d.data() }));
  return results; // already ascending
}

export function buildNetWorthSeries(history) {
  return (history || []).map(h => ({
    date: h.id,
    value: Number(h.portfolio?.summary?.patrimonioTotal ?? 0)
  }));
}

export function computeYTD(history, cutoffDateId) {
  if (!history || history.length === 0) return { amount: 0, percent: 0 };
  const cutoff = cutoffDateId || history[history.length - 1]?.id;
  if (!cutoff) return { amount: 0, percent: 0 };
  const [targetYearStr] = cutoff.split('-');
  const targetYear = Number(targetYearStr);
  const inYear = history.filter(h => {
    const [y] = h.id.split('-');
    return Number(y) === targetYear && (!cutoffDateId || h.id <= cutoffDateId);
  });
  if (inYear.length === 0) return { amount: 0, percent: 0 };
  const first = inYear[0].portfolio?.summary?.patrimonioTotal ?? 0;
  const last = inYear[inYear.length - 1].portfolio?.summary?.patrimonioTotal ?? 0;
  const amount = last - first;
  const percent = first > 0 ? (amount / first) * 100 : 0;
  return { amount, percent };
}

export function computeMTD(history, cutoffDateId) {
  if (!history || history.length === 0) return { amount: 0, percent: 0 };
  const cutoff = cutoffDateId || history[history.length - 1]?.id;
  if (!cutoff) return { amount: 0, percent: 0 };
  const base = new Date(cutoff + 'T00:00:00Z');
  const targetMonth = base.getUTCMonth();
  const targetYear = base.getUTCFullYear();
  const inMonth = history.filter(h => {
    const d = new Date(h.id + 'T00:00:00Z');
    return d.getUTCFullYear() === targetYear && d.getUTCMonth() === targetMonth && (!cutoffDateId || h.id <= cutoffDateId);
  });
  if (inMonth.length === 0) return { amount: 0, percent: 0 };
  const first = inMonth[0].portfolio?.summary?.patrimonioTotal ?? 0;
  const last = inMonth[inMonth.length - 1].portfolio?.summary?.patrimonioTotal ?? 0;
  const amount = last - first;
  const percent = first > 0 ? (amount / first) * 100 : 0;
  return { amount, percent };
}

// Compute pure return for Month-To-Date based on portfolio summaries (gananciaPerdidaMes)
export function computeReturnMTD(history, cutoffDateId) {
  if (!history || history.length === 0) return { amount: 0, percent: 0 };
  const cutoff = cutoffDateId || history[history.length - 1]?.id;
  if (!cutoff) return { amount: 0, percent: 0 };
  const base = new Date(cutoff + 'T00:00:00Z');
  const targetMonth = base.getUTCMonth();
  const targetYear = base.getUTCFullYear();
  // Latest snapshot within month up to cutoff
  const inMonth = history.filter(h => {
    const d = new Date(h.id + 'T00:00:00Z');
    return d.getUTCFullYear() === targetYear && d.getUTCMonth() === targetMonth && (!cutoffDateId || h.id <= cutoffDateId);
  });
  if (inMonth.length === 0) return { amount: 0, percent: 0 };
  const lastInMonth = inMonth[inMonth.length - 1];
  const amount = Number(lastInMonth.portfolio?.summary?.gananciaPerdidaMes ?? 0);
  // Find patrimony at start of month (last snapshot before YYYY-MM-01)
  const monthStartId = `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-01`;
  const beforeMonth = history.filter(h => h.id < monthStartId);
  const baseSnapshot = beforeMonth.length > 0 ? beforeMonth[beforeMonth.length - 1] : inMonth[0];
  const basePat = Number(baseSnapshot?.portfolio?.summary?.patrimonioTotal ?? 0);
  const percent = basePat > 0 ? (amount / basePat) * 100 : 0;
  return { amount, percent };
}

// Compute pure return for Year-To-Date by summing each month's gananciaPerdidaMes up to cutoff
export function computeReturnYTD(history, cutoffDateId) {
  if (!history || history.length === 0) return { amount: 0, percent: 0 };
  const cutoff = cutoffDateId || history[history.length - 1]?.id;
  if (!cutoff) return { amount: 0, percent: 0 };
  const [targetYearStr] = cutoff.split('-');
  const targetYear = Number(targetYearStr);
  const inYear = history.filter(h => {
    const [y] = h.id.split('-');
    return Number(y) === targetYear && (!cutoffDateId || h.id <= cutoffDateId);
  });
  if (inYear.length === 0) return { amount: 0, percent: 0 };
  // Keep last snapshot per month, then sum its monthly return
  const byMonth = new Map();
  for (const h of inYear) {
    const month = h.id.slice(0, 7);
    const cur = byMonth.get(month);
    if (!cur || h.id > cur.id) byMonth.set(month, h);
  }
  const amount = Array.from(byMonth.values()).reduce((sum, h) => sum + Number(h.portfolio?.summary?.gananciaPerdidaMes ?? 0), 0);
  // Find patrimony at end of last year (last snapshot before YYYY-01-01)
  const yearStartId = `${targetYear}-01-01`;
  const beforeYear = history.filter(h => h.id < yearStartId);
  const baseSnapshot = beforeYear.length > 0 ? beforeYear[beforeYear.length - 1] : inYear[0];
  const basePat = Number(baseSnapshot?.portfolio?.summary?.patrimonioTotal ?? 0);
  const percent = basePat > 0 ? (amount / basePat) * 100 : 0;
  return { amount, percent };
}

export function buildProductAllocation(latest) {
  const cuentas = latest?.portfolio?.summary?.cuentas || [];
  return cuentas.map(c => ({ label: c.nombre, value: Number(c.patrimonio || 0) }));
}

export function buildCurrencyBreakdown(latest) {
  const accounts = latest?.portfolio?.accounts || [];
  const totals = new Map();
  for (const acc of accounts) {
    for (const p of acc.posiciones || []) {
      const cur = (p.moneda || 'CLP').toUpperCase();
      const v = Number(p.valorizacion || 0);
      totals.set(cur, (totals.get(cur) || 0) + (Number.isFinite(v) ? v : 0));
    }
  }
  return Array.from(totals.entries()).map(([code, value]) => ({ label: code, value }));
}

export function buildPositions(latest) {
  const rows = [];
  for (const acc of latest?.portfolio?.accounts || []) {
    for (const p of acc.posiciones || []) {
      rows.push({
        account: acc.nombreCuenta,
        nombre: p.nombre,
        nemo: p.nemo,
        moneda: p.moneda,
        titulos: p.titulos,
        precio: p.precio,
        valorizacion: p.valorizacion,
        rentabilidadPct: p.rentabilidadPct
      });
    }
  }
  return rows;
}


